import { usePermissions } from '@40seas/frontend/dashboard/hooks';
import { Resources } from '../resources/types';
import { Permissions } from './types';
import { CUSTOM_READ_PERMISSIONS } from './customReadPermissions';

export const hasAccess = (permissions: string[], resource: string, ...actions: string[]): boolean => {
    // Check for deny permissions first
    if (hasDenyPermission(permissions, resource, actions)) {
        return false;
    }

    // Check for full admin or full resource permissions
    if (hasAdminPermissions(permissions) || permissions.some((p) => p === `${resource}:full`)) {
        return true;
    }

    const hasFullReadPermission = hasAdminReadPermissions(permissions);

    // Check for action permissions
    for (const action of actions) {
        if (hasFullReadPermission && isReadAction(resource, action)) {
            return true;
        }

        if (permissions.some((p) => p === `${resource}:${action}` || p === `${resource}:${action.toLowerCase()}`)) {
            return true;
        }
    }

    return false;
};

export const hasAdminPermissions = (permissions: string[]) => {
    return permissions.some((p) => p === 'admin:full');
};

export const hasAdminReadPermissions = (permissions: string[]) => {
    return permissions.some((p) => ['admin:full', 'admin:query'].includes(p));
};

export const hasDeny = (permissions: string[], resource: string) => {
    return permissions.some((p) => p === `dashboard:${resource}:deny`) && !permissions.some((p) => p === `dashboard:${resource}:allow`);
};

export const hasTabDeny = (permissions: string[], resource: string) => {
    return permissions.some((p) => p === `tab:${resource}:deny`) && !permissions.some((p) => p === `tab:${resource}:allow`);
};

export const hasListTabDeny = (permissions: string[], resource: string, tab: string) => {
    return permissions.some((p) => p === `tab:${resource}:${tab}:deny`) && !permissions.some((p) => p === `tab:${resource}:${tab}:allow`);
};

export const hasDashboardAccess = (permissions: string[], resource: string) => {
    return hasAccess(permissions, resource, 'query') && !hasDeny(permissions, resource);
};

export const hasTabAccess = (permissions: string[], resource: string) => {
    return hasAccess(permissions, resource, 'query') && !hasTabDeny(permissions, resource);
};

const hasOnePermissionFrom = (permissionArray: string[]) => {
    const { permissions, isLoading } = usePermissions<string[]>();

    if (isLoading) return false;

    const hasFullReadPermission = hasAdminReadPermissions(permissions);

    // Deny check must be evaluated across all entries first — a broad grant like
    // admin:full must not bypass a deny on the specific resource:action being checked.
    const hasPermissionDeny = permissionArray.some((permission) => {
        const [resource, action] = permission.split(':');
        return action && hasDenyPermission(permissions, resource, [action]);
    });

    if (hasPermissionDeny) return false;

    return permissionArray.some((permission) => {
        const [resource, action] = permission.split(':');
        // Check if granted via admin read permissions
        if (hasFullReadPermission && action && isReadAction(resource, action)) return true;
        // Check if directly granted
        return permissions.includes(permission.toLowerCase());
    });
};

const hasAllPermissionsFrom = (permissionArray: string[]) => {
    if (permissionArray.length === 0) return false;

    const { permissions, isLoading } = usePermissions<string[]>();

    if (isLoading) return false;

    const hasFullReadPermission = hasAdminReadPermissions(permissions);

    return permissionArray.every((permission) => {
        const [resource, action] = permission.split(':');
        // Check deny permission for this specific entry
        if (action && hasDenyPermission(permissions, resource, [action])) return false;
        // Check if granted via admin read permissions
        if (hasFullReadPermission && action && isReadAction(resource, action)) return true;
        // Check if directly granted
        return permissions.includes(permission.toLowerCase());
    });
};

export const useIsAdmin = () => {
    const { permissions, isLoading } = usePermissions<string[]>();

    if (isLoading) return false;

    return hasAdminReadPermissions(permissions);
};

export const useIsFullAdmin = () => {
    const { permissions, isLoading } = usePermissions<string[]>();

    if (isLoading) return false;

    return hasAdminPermissions(permissions);
};

export const hasPermission = (permission: string) => {
    const { permissions, isLoading } = usePermissions<string[]>();

    if (isLoading) return false;

    return permissions.includes(permission);
};

export const hasDashboardPermission = (permissions: string[], resource: string) => {
    return permissions.some((p) => p.startsWith(resource)) && !hasDeny(permissions, resource);
};

export const isResourceHasPermission = (resource: Resources | string, permission: Permissions | string) => {
    return hasOnePermissionFrom(['admin:full', `${resource}:full`, `${resource}:${permission}`]);
};

export const hasChargeInvoicePermission = () => {
    return hasOnePermissionFrom(['admin:full', 'charges/invoice:full']);
};

export const hasDeveloperPermission = () => {
    return hasOnePermissionFrom(['admin:full', 'users:credentials']);
};

export const hasDashboardTestModePermission = () => {
    return hasAllPermissionsFrom(['environment:sandbox', 'environment:live']);
};

export const hasInviteSuppliersPermissions = () => {
    return hasOnePermissionFrom(['users:supplierInvite']);
};

export const hasInvitePermissions = () => {
    return hasOnePermissionFrom(['users:invite', 'admin:full']);
};

export const hasCrudPermissions = (resource: string): [boolean, boolean, boolean, boolean] => {
    const createPermission = hasOnePermissionFrom(['admin:full', `${resource}:full`, `${resource}:create`]);
    const readPermission = hasOnePermissionFrom(['admin:full', `${resource}:full`, `${resource}:query`]);
    const updatePermission = hasOnePermissionFrom(['admin:full', `${resource}:full`, `${resource}:update`]);
    const deletePermission = hasOnePermissionFrom(['admin:full', `${resource}:full`, `${resource}:delete`]);

    return [createPermission, readPermission, updatePermission, deletePermission];
};

const hasDenyPermission = (permissions: string[], resource: string, actions: string[]): boolean => {
    if (permissions.includes(`${resource}:full:deny`)) return true;
    return actions?.some((action) => permissions.includes(`${resource}:${action.toLowerCase()}:deny`));
};

/**
 * Checks if the given resource:action represents a read-only operation.
 * Returns true for: standard actions (get, query, ready, health) or custom GET endpoints.
 */
export const isReadAction = (resource: string, action: string): boolean => {
    const standardReadActions = ['get', 'query', 'ready', 'health'];

    if (standardReadActions.includes(action.toLowerCase())) {
        return true;
    }

    return CUSTOM_READ_PERMISSIONS.includes(`${resource}:${action.toLowerCase()}`);
};
